/**
 * TanStack Query 키 SSoT — 모든 서버 상태 키는 여기 팩토리로만 만든다(`queryKey: [...]` 리터럴은 ESLint 가 막는다).
 *
 * 한 곳에 두는 이유는 무효화다. 업체를 바꾸면 판정을 보여 주는 다른 기능(캘린더·대시보드·사업 상세·매칭 검색)도
 * 낡는데, 기능별 파일에 키가 흩어지면 그 의존을 빠뜨린다. 의존 묶음은 `COMPANY_DEPENDENT_KEYS` 가 소유한다.
 * 키 모양은 `['도메인', 하위…]` — 상위 접두로 무효화하면 하위가 모두 걸린다.
 */

export const authKeys = {
  verifyEmail: (token: string) => ['auth', 'verify-email', token] as const,
};

/** 내 계정 설정(specs/080) — 회원 본인 단위라 업체 변경과 무관하다. */
export const accountKeys = {
  notificationPreference: ['account', 'notification-preference'] as const,
};

export const announcementKeys = {
  all: ['announcement'] as const,
  detail: (id: number) => ['announcement', 'detail', id] as const,
};

/** 공고 찾기(specs/031) — 목록·검색 모드 공용. 공고 단위 결과라 업체와 무관하다(업체 변경 무효화 대상 아님). */
export const finderKeys = {
  all: ['finder'] as const,
  find: (params: object) => ['finder', 'find', params] as const,
};

export const consultantAnnouncementKeys = {
  all: ['consultant-announcement'] as const,
  list: (condition: object) => ['consultant-announcement', 'list', condition] as const,
  detail: (id: number) => ['consultant-announcement', 'detail', id] as const,
  infinite: (condition: object) => ['consultant-announcement', 'infinite', condition] as const,
};

export const companyKeys = {
  all: ['company'] as const,
  list: (condition: object) => ['company', 'list', condition] as const,
  detail: (id: number) => ['company', 'detail', id] as const,
  /** 내 회사 1곳(기업 회원, specs/065) — `company` 접두라 등록·수정 무효화에 함께 걸린다. */
  mine: ['company', 'mine'] as const,
  judgements: (id: number) => ['company', 'detail', id, 'judgements'] as const,
  /** 업체 성장 로드맵(specs/061) */
  growthRoadmap: (id: number) => ['company', 'detail', id, 'growth-roadmap'] as const,
  judgementPage: (id: number, params: object) =>
    ['company', 'detail', id, 'judgements', params] as const,
  /** 기업 공고 찾기 누적 목록·요약(specs/069) — `company` 접두라 업체 수정·답 저장 무효화에 함께 걸린다. */
  judgementInfinite: (id: number, params: object) =>
    ['company', 'detail', id, 'judgements', 'infinite', params] as const,
  judgementDigest: (id: number, year: number | undefined) =>
    ['company', 'detail', id, 'judgements', 'digest', year ?? null] as const,
  /** 모바일 무한 목록(specs/037) — `list` 와 모양이 달라(페이지 누적) 키를 나눈다. `company` 접두라 업체 변경 무효화에 걸린다. */
  infiniteList: (keyword: string) => ['company', 'infinite-list', keyword] as const,
  /** 업체 관심 사업(specs/079) — `company` 접두라 업체 변경 무효화에 함께 걸린다. */
  interests: (id: number) => ['company', 'detail', id, 'interests'] as const,
  /** 이미 등록된 사업자등록번호 확인(모바일 등록 확인 화면) — 등록 후 무효화되어 다시 확인된다. */
  duplicate: (bizRegNo: string) => ['company', 'duplicate', bizRegNo] as const,
};

/** 사업 연도 선택지(specs/032) — 헤더 연도 선택기·전역 기준 연도의 원천. */
export const programYearKeys = {
  list: ['program-year', 'list'] as const,
};

export const programKeys = {
  all: ['program'] as const,
  detail: (programYearId: number, companyId?: number) =>
    ['program', 'detail', programYearId, companyId ?? null] as const,
  targetCompanies: (programYearId: number) =>
    ['program', 'target-companies', programYearId] as const,
};

export const calendarKeys = {
  all: ['calendar'] as const,
  combined: (params: object) => ['calendar', 'combined', params] as const,
  yearly: (params: object) => ['calendar', 'yearly', params] as const,
  filterOptions: (year: number | null) => ['calendar', 'filter-options', year] as const,
  /** 업체별 캘린더(기업 홈 다가오는 마감, specs/069) */
  company: (companyId: number, params: object) =>
    ['calendar', 'company', companyId, params] as const,
};

/** 예비창업 진단(specs/072) — 공개, 질문 문구·대상 유무 */
export const diagnosisKeys = {
  questions: ['diagnosis', 'questions'] as const,
};

export const dashboardKeys = {
  all: ['dashboard'] as const,
  tasks: (year: number | undefined, recommendedFirst = true) =>
    ['dashboard', 'tasks', year ?? null, recommendedFirst] as const,
};

/** 매칭 검색 — 고객사 기준(내 업체 "맞는 공고")·지원사업 기준(공고 상세 "지원 가능한 내 고객사")·근거 요약. */
export const searchKeys = {
  all: ['search'] as const,
  company: (params: object | null) => ['search', 'company', params] as const,
  program: (announcementId: number | null) => ['search', 'program', announcementId] as const,
  summary: (query: string | null, chunkIds: readonly number[]) =>
    ['search', 'summary', query, chunkIds] as const,
  /** 업체가 들어가는 검색(고객사 기준·지원사업 기준 후보) 접두 — 업체 변경 시 무효화. */
  companyScoped: [
    ['search', 'company'],
    ['search', 'program'],
  ] as const,
};

/** 비로그인 공개 화면(specs/070) */
export const publicKeys = {
  announcements: (params: object) => ['public', 'announcements', params] as const,
  announcement: (id: number) => ['public', 'announcement', id] as const,
  featured: ['public', 'featured-programs'] as const,
};

export const landingKeys = {
  betaFreePeriod: ['landing', 'beta-free-period'] as const,
  stats: ['landing', 'stats'] as const,
  announcements: (category: string) => ['landing', 'announcements', category] as const,
  askExamples: ['landing', 'ask-examples'] as const,
  askResult: (key: string) => ['landing', 'ask-result', key] as const,
};

export const subscriptionKeys = {
  all: ['subscription'] as const,
  me: ['subscription', 'me'] as const,
  paymentMethod: ['subscription', 'payment-method'] as const,
  callback: (mode: string, authKey: string | null, orderId: string | null) =>
    ['subscription', 'callback', mode, authKey, orderId] as const,
};

/**
 * 업체 등록·수정·삭제 시 함께 낡는 키 — 업체 목록·판정을 보여 주는 화면 전부.
 * 공고 찾기(`finder`)는 업체와 무관해 빼고, 업체가 들어가는 검색만 넣는다(고객사 기준은 업체 지역을 기본 필터로 쓴다).
 */
export const COMPANY_DEPENDENT_KEYS = [
  companyKeys.all,
  calendarKeys.all,
  dashboardKeys.all,
  programKeys.all,
  ...searchKeys.companyScoped,
] as const;
